import type { RouteCandidate } from '@/features/network/models';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatCapacity, formatLatency, formatMoney } from '@/lib/format';
import { RoutePills } from '@/components/RoutePills';
import type { RouteSolverProgress } from '@/features/routing/useRouteSolver';

export function CandidatesList({
  routes,
  loading,
  progress,
  selectedRouteId,
  onSelectRouteForGraph,
  onExportJson,
  onExportXlsx,
}: {
  routes: RouteCandidate[];
  loading: boolean;
  progress: RouteSolverProgress;
  selectedRouteId: string | null;
  onSelectRouteForGraph: (route: RouteCandidate) => void;
  onExportJson: () => void;
  onExportXlsx: () => void;
}) {
  const progressValue = Math.max(2, Math.min(100, progress.percent));

  return (
    <Card className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-lg font-semibold">Кандидаты маршрутов</div>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            Отсортировано по выбранному режиму. В Pareto режиме показываются только недоминируемые варианты.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={onExportJson} disabled={routes.length === 0}>
            Экспорт JSON
          </Button>
          <Button type="button" variant="secondary" onClick={onExportXlsx} disabled={routes.length === 0}>
            Экспорт XLSX
          </Button>
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="rounded-3xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 dark:border-slate-800">
            <div className="font-medium text-slate-700 dark:text-slate-200">Идёт расчёт маршрутов…</div>
            <div className="mt-2 text-xs">{progress.message} · {progress.percent}%</div>
            <div className="mx-auto mt-4 h-2 w-full max-w-lg overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div
                className="h-full rounded-full bg-slate-900 transition-[width] duration-200 ease-out dark:bg-slate-100"
                style={{ width: `${progressValue}%` }}
              />
            </div>
          </div>
        ) : routes.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 dark:border-slate-800">
            Загрузи файл и выбери пару точек, чтобы получить список маршрутов.
          </div>
        ) : (
          <div className="space-y-4">
            {routes.map((route, index) => (
              <div key={route.id} className="rounded-3xl border border-slate-200 p-4 dark:border-slate-800">
                <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Route #{index + 1}</div>
                    <div className="mt-1 text-xs text-slate-500">Score: {route.score.toFixed(4)}</div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge>{route.hops} hop(s)</Badge>
                    <Badge>{formatLatency(route.totalLatencyMs)}</Badge>
                    <Badge>MRC {formatMoney(route.totalMrc)}</Badge>
                    <Badge>Cap {formatCapacity(route.bottleneckMbps)}</Badge>
                  </div>
                </div>

                <RoutePills nodes={route.nodes} />

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant={selectedRouteId === route.id ? 'primary' : 'secondary'}
                    onClick={() => onSelectRouteForGraph(route)}
                  >
                    {selectedRouteId === route.id ? 'На графе' : 'Показать на графе'}
                  </Button>
                  {route.vendors.map((vendor) => (
                    <Badge key={vendor} className="border-slate-900 bg-slate-900 text-white dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900">
                      {vendor}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
